import React from "react";

// Reusable icon box matching the design's blue gradient style
const IconBox = ({ icon }) => (
  <div
    className="w-10 h-10 sm:w-[48px] sm:h-[48px] md:w-[56px] md:h-[56px] rounded-[10px] sm:rounded-[14px] md:rounded-[16px] flex items-center justify-center shadow-lg shadow-[#1a73e8]/20 flex-shrink-0"
    style={{
      background: "radial-gradient(circle at center, #3b82f6 0%, #0066ff 100%)",
    }}
  >
    <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex items-center justify-center">
      {icon}
    </div>
  </div>
);

// Left text panel inside each bento container
const InfoPanel = ({
  icon,
  title,
  desc,
  mobileWidth = "w-[65%] sm:w-[65%] md:w-[48%]",
  desktopWidth = "lg:w-[48%]",
}) => (
  <div
    className={`flex flex-col justify-between p-3 sm:p-4 md:p-6 lg:p-7 ${mobileWidth} ${desktopWidth} h-full gap-3 sm:gap-4 md:gap-0`}
  >
    <IconBox icon={icon} />
    <div className="flex flex-col gap-1 sm:gap-1.5 md:gap-2 lg:mt-4">
      <h3 className="text-[14px] sm:text-[18px] md:text-[16px] lg:text-[22px] font-bold text-slate-900 tracking-tight leading-tight">
        {title}
      </h3>
      <p className="text-[#64748b] text-[11px] sm:text-[14px] md:text-[14px] lg:text-[16px] font-medium leading-[1.35] sm:leading-[1.4] md:leading-[1.5] lg:leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// Photo panel inside each bento container
const PhotoPanel = ({ src, alt }) => (
  <div className="relative flex-1 h-[140px] xs:h-[160px] sm:h-[180px] md:h-auto overflow-hidden rounded-[24px] md:rounded-[20px] m-2 md:m-3 my-auto">
    <img
      src={src}
      alt={alt}
      className="absolute inset-0 w-full h-full object-cover"
    />
  </div>
);

const HowPracticesUse = () => {
  return (
    <section className="py-20 md:py-24 px-3 sm:px-4 lg:px-6 max-w-7xl mx-auto">
      {/* Section Header */}
      <div className="text-center mb-10 md:mb-14 px-4">
        <h2 className="text-[32px] md:text-[42px] font-bold text-slate-900 mb-3 tracking-tight leading-tight px-2">
          4 Ways Dental Practices Use AI Smile Simulation
        </h2>
        <p className="text-slate-500 text-[16px] md:text-[17px] font-medium max-w-2xl mx-auto">
          From first consultation to case re-activation — SmileReveal fits into
          every stage of the patient journey
        </p>
      </div>

      {/* Bento grid — 2 columns on all screens but with different row styles */}
      <div className="flex flex-col gap-4">
        {/* ── Row 1: two equal containers ── */}
        <div className="grid grid-cols-1 md:grid-cols-[6fr_5fr] gap-3 md:gap-4 md:h-[260px]">
          {/* Container 1 — Generate Simulation */}
          <div className="bg-white rounded-[24px] md:rounded-[28px] border border-[#f1f5f9] shadow-[0_2px_16px_rgba(0,0,0,0.02)] flex min-h-[160px] md:h-full overflow-hidden group transition-all duration-300 hover:shadow-[0_8px_32px_rgba(0,0,0,0.07)] hover:-translate-y-0.5 hover:border-blue-100">
            <InfoPanel
              icon={
                <img
                  src="/icons/doctor.png"
                  alt="Cosmetic & Implants"
                  className="w-full h-full object-contain p-0.5"
                />
              }
              title="Cosmetic & Implants"
              desc="Show results for makeovers, implants, or veneers before patients commit."
            />
            <PhotoPanel
              src="/doctor_promising.webp"
              alt="Practitioner using VR for smile simulation"
            />
          </div>

          {/* Container 2 — Pre-sell Treatment */}
          <div className="bg-white rounded-[24px] md:rounded-[28px] border border-[#f1f5f9] shadow-[0_2px_16px_rgba(0,0,0,0.02)] flex min-h-[160px] md:h-full overflow-hidden group transition-all duration-300 hover:shadow-[0_8px_32px_rgba(0,0,0,0.07)] hover:-translate-y-0.5 hover:border-blue-100">
            <InfoPanel
              icon={
                <img
                  src="/icons/presell.png"
                  alt="High-Value Cases"
                  className="w-full h-full object-contain p-0.5"
                />
              }
              title="High-Value Cases"
              desc="Build visual confidence for $20K+ All-on-X plans before financing."
            />
            <PhotoPanel
              src="/doctor_showing_transformation.webp"
              alt="Dentist presenting treatment plan"
            />
          </div>
        </div>

        {/* ── Row 2: symmetric columns for mobile, asymmetric columns for desktop ── */}
        <div className="grid grid-cols-1 md:grid-cols-[5fr_6fr] gap-3 md:gap-4 md:h-[260px]">
          {/* Container 3 — Visual Anchor */}
          <div className="bg-white rounded-[24px] md:rounded-[28px] border border-[#f1f5f9] shadow-[0_2px_16px_rgba(0,0,0,0.02)] flex min-h-[160px] md:h-full overflow-hidden group transition-all duration-300 hover:shadow-[0_8px_32px_rgba(0,0,0,0.07)] hover:-translate-y-0.5 hover:border-blue-100">
            <InfoPanel
              icon={
                <img
                  src="/icons/anchor.png"
                  alt="Visual Decision Aid"
                  className="w-full h-full object-contain p-0.5"
                />
              }
              title="Visual Decision Aid"
              desc="Provide a tangible image for patients to share with family and decision-makers."
            />
            <PhotoPanel
              src="/doctor_nurse_analysis.webp"
              alt="Doctor reviewing options with patient"
            />
          </div>

          {/* Container 4 — Re-activate Cases */}
          <div className="bg-white rounded-[24px] md:rounded-[28px] border border-[#f1f5f9] shadow-[0_2px_16px_rgba(0,0,0,0.02)] flex min-h-[160px] md:h-full overflow-hidden group transition-all duration-300 hover:shadow-[0_8px_32px_rgba(0,0,0,0.07)] hover:-translate-y-0.5 hover:border-blue-100">
            <InfoPanel
              icon={
                <img
                  src="/icons/generate_sim.png"
                  alt="Re-activate Stalled Cases"
                  className="w-full h-full object-contain p-0.5"
                />
              }
              title="Re-activate Stalled Cases"
              desc="Send AI previews to 'maybe' patients to re-engage and convert."
              desktopWidth="md:w-[44%]"
            />
            <PhotoPanel
              src="/doctor_comforting.webp"
              alt="Patient reviewing simulation results"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowPracticesUse;
